import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@ets/design-system';

import { useSelector, useSuggestions } from '_common/hooks';

import { Textarea, TextareaAPI, type TextareaProps } from '_common/lib/components';
import { SuiteAvatar } from '_common/suite/components';

import styles from './EditableReplyCard.module.scss';

type EditableReplyCardProps = {
  currentContent?: RichTextJSON;
  collaborators: UserId[];
  onCancel: () => void;
  onApply: (newContent: RichTextJSON) => void;
  testId: string;
};

const EditableReplyCard = ({
  currentContent,
  collaborators,
  onCancel,
  onApply,
  testId,
}: EditableReplyCardProps) => {
  const intl = useIntl();
  const suggestions = useSuggestions(collaborators);

  const currentUserId = useSelector((state) => state.auth.userId);

  const replyRef = useRef<TextareaAPI>(null);
  const [replyContent, setReplyContent] = useState(currentContent);

  const handleEditReply = () => {
    if (!replyContent) {
      return;
    }

    onApply(replyContent);
  };

  const handleReplyChange: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    setReplyContent(target.value as unknown as RichTextJSON);
  };

  return (
    <div className={styles.root} data-testid={testId ? `${testId}-editable` : undefined}>
      <div className={styles.body}>
        <SuiteAvatar userId={currentUserId} />
        <Textarea
          name="reply"
          size="m"
          type="json"
          ref={replyRef}
          initialContent={currentContent}
          mentions={suggestions}
          noMentionsLabel={intl.formatMessage({ id: 'NO_USERS_AVAILABLE' })}
          placeholder={intl.formatMessage({ id: 'REPLY_PLACEHOLDER' })}
          onChange={handleReplyChange}
          variant="compact"
          testId={testId}
          shouldFocus
        />
      </div>
      <div className={styles.footer}>
        <Button variant="link" size="s" onClick={onCancel} testId={`${testId}-cancel`}>
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          variant="primary"
          size="s"
          onClick={handleEditReply}
          disabled={!replyContent}
          testId={`${testId}-apply`}
        >
          <FormattedMessage id={!currentContent ? 'global.create' : 'global.edit'} />
        </Button>
      </div>
    </div>
  );
};

export default EditableReplyCard;
